// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Skeleton from "@awc/App/Components/Skeleton/Skeleton";
import { CustomAppState } from "@awc/App/Store/Reducers";
import StyledShadowBox, { StyledShadowBoxStyles } from "@awc/XDL/Components/Common/ShadowBox";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    // <XNG-Changes>
    const pagination = getProductListDataViewProperty(state, "pagination");
    const hasProducts = pagination?.totalItemCount;
    // </XNG-Changes>

    return {
        view: state.pages.productList.view,
        // XNG-Changes
        hasProducts,
        isLoading: state.pages.productList.isLoading,
    };
};

type Props = ReturnType<typeof mapStateToProps> & WidgetProps;

export interface ProductListControlsContainerStyles {
    container?: GridContainerProps;
    countGridItem?: GridItemProps;
    countTableViewGridItem?: GridItemProps;
    sortGridItem?: GridItemProps;
    sortTableViewGridItem?: GridItemProps;
    filterGridItem?: GridItemProps;
    filterTableViewGridItem?: GridItemProps;
    viewSelectGridItem?: GridItemProps;
    viewSelectTableViewGridItem?: GridItemProps;
    // XNG-Changes
    shadowBox?: StyledShadowBoxStyles;
}

export const controlsContainerStyles: ProductListControlsContainerStyles = {
    container: {
        gap: 0,
    },
    countGridItem: {
        width: [12, 12, 2, 5, 6],
    },
    countTableViewGridItem: {
        width: [12, 12, 1, 1, 1],
    },
    sortGridItem: {
        width: [12, 12, 5, 4, 4],
    },
    sortTableViewGridItem: {
        width: [12, 12, 5, 4, 3],
    },
    filterGridItem: {
        width: 0,
    },
    filterTableViewGridItem: {
        width: [0, 0, 2, 2, 2],
    },
    viewSelectGridItem: {
        width: [0, 0, 5, 3, 2],
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    viewSelectTableViewGridItem: {
        width: [0, 0, 4, 5, 6],
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
};

const styles = controlsContainerStyles;

const ProductListControlsContainer: FC<Props> = ({ id, view, hasProducts, isLoading }) => {
    const shellContext = useShellContext();

    // <XNG-Changes>
    if (isLoading) {
        return <Skeleton height="27px" {...styles.container} />;
    }

    // <XNG-Changes>
    if (!hasProducts) {
        return null;
    }

    const isTableView = view?.toLowerCase() === "table";
    // </XNG-Changes>

    // XNG-Changes: structure change
    return (
        <StyledShadowBox extendedStyles={styles.shadowBox}>
            <StyledWrapper className="pagination-sort-by">
                <Zone contentId={id} zoneName="Content00" />
            </StyledWrapper>
            <StyledWrapper>
                <Zone contentId={id} zoneName="Content01" />
            </StyledWrapper>
            <StyledWrapper className="hide-on-mobile">
                <Zone contentId={id} zoneName="Content02" />
            </StyledWrapper>
            {(isTableView || shellContext?.isEditing) && (
                <StyledWrapper className="hide-on-mobile">
                    <Zone contentId={id} zoneName="Content03" />
                </StyledWrapper>
            )}
            <StyledWrapper className="pagination-page-number">
                <Zone contentId={id} zoneName="Content04" />
            </StyledWrapper>
        </StyledShadowBox>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ProductListControlsContainer),
    definition: {
        group: "Product List",
        displayName: "List Controls Container",
        // XNG-Changes: add CategoryDetailsPage
        allowedContexts: ["ProductListPage", "CategoryDetailsPage"],
    },
};

export default widgetModule;
